import { useQuery } from '@tanstack/react-query';

import { api, type MatchedLocation, type MatchedRequirement } from '../lib/api.js';
import { useModalFocus } from '../lib/useModalFocus.js';
import { Coverage, WhyItMatches } from './MatchedLocations.js';
import { LocationMap } from './LocationMap.js';
import { locationMetaLine } from './LocationCard.js';
import { PhotoViewer } from './PhotoViewer.js';
import { ConversationEntry } from './conversations/ConversationEntry.js';
import { ReferencesCountLink } from './references/ReferencesCountLink.js';
import { RejectWithReason } from './RejectWithReason.js';

/**
 * ONE LOCATION, LOOKED AT PROPERLY (owner review 2026-10-27, points 6, 7 and 9).
 *
 * The Explore card carries one thumbnail, which is enough to recognise a place
 * and not enough to judge it. This is where a producer judges it: every
 * photograph the listing page shows, large; the same verdicts as the card —
 * coverage with its denominator and the axes beside it, never a score
 * (`script-breakdown/CLAUDE.md`, "Coverage is not fit"); and where it is, on
 * the same map the Locations tab uses.
 *
 * THE GALLERY IS READ WHEN THE PANEL OPENS, never with the list: the matched
 * view stays one thumbnail per card, and the server answers only for a listed
 * candidate of this production. A scout's find has no photographs to show a
 * producer and no exact position (ADR 0012), and the panel says so instead of
 * drawing an empty frame.
 *
 * SHORTLIST AND REJECT ARE HERE TOO, the same handlers as the card, because
 * the moment a producer has decided is the moment they are looking at this.
 */
export function LocationDetailPanel({
  projectId,
  location,
  hit,
  busy = false,
  shortlistRefusal = null,
  rejectRefusal = null,
  onShortlist,
  onReject,
  onRestore,
  onClose,
}: {
  projectId: string;
  location: MatchedLocation;
  hit: MatchedRequirement;
  busy?: boolean;
  shortlistRefusal?: string | null;
  rejectRefusal?: string | null;
  onShortlist: () => void;
  /** With the reason the API requires (`RejectWithReason`). */
  onReject: (reason: string) => void;
  /** A rejected place back to the candidates; absent, the panel offers no way back. */
  onRestore?: () => void;
  onClose: () => void;
}) {
  const modal = useModalFocus<HTMLDivElement>(onClose);
  const isListing = location.sourceType === 'LOCATION';

  const gallery = useQuery({
    queryKey: ['location-photos', projectId, location.sourceId],
    queryFn: () => api.locationPhotos(projectId, location.sourceId),
    enabled: isListing,
    staleTime: 60_000,
  });

  // The card's own photograph while the gallery loads, or if it cannot be
  // read: something to look at rather than a blank frame.
  const photos =
    gallery.data?.photos ?? (location.imageUrl === null ? [] : [{ url: location.imageUrl, cover: true }]);

  const shortlisted = hit.candidateStatus === 'SHORTLIST';
  const rejected = hit.candidateStatus === 'REJECTED';

  return (
    <div
      data-testid="location-detail"
      onKeyDown={modal.onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-detail-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35, 35, 35, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '4vh 16px',
        overflowY: 'auto',
        zIndex: 40,
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={modal.ref}
        style={{
          background: 'var(--color-bg)',
          width: 'min(860px, 100%)',
          border: '1px solid var(--color-divider)',
          boxShadow: '0 18px 48px rgba(16, 24, 40, 0.18)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '16px 20px',
            borderBottom: '2px solid var(--ink)',
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 id="location-detail-title" style={{ margin: 0, fontSize: 20, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
              {location.title}
            </h2>
            <p className="mm" style={{ margin: '4px 0 0', fontSize: 13 }} data-testid="location-detail-meta">
              {locationMetaLine(location)}
            </p>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <ConversationEntry
                projectId={projectId}
                anchorType="PLACE"
                anchorId={String(location.sourceId)}
                label={location.title}
              />
              <ReferencesCountLink projectId={projectId} type="PLACE" id={String(location.sourceId)} />
            </div>
          </div>
          <button
            type="button"
            className="ctl"
            data-testid="location-detail-close"
            aria-label="Close"
            onClick={onClose}
            style={{ minWidth: 36, minHeight: 36 }}
          >
            ×
          </button>
        </header>

        {/* `minmax(0, 1fr)`, not the implicit `auto` column: an auto column grows to its
            content's minimum width, and the photo strip's minimum is every thumbnail side by
            side — fifteen photos pushed the gallery and the map out past the panel. */}
        <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>
          {isListing ? (
            <>
              <PhotoViewer photos={photos} title={location.title} />
              {gallery.isError ? (
                <p className="mm" data-testid="location-detail-gallery-error" style={{ margin: 0, fontSize: 12.5 }}>
                  The full gallery could not be loaded — showing the listing&apos;s cover only.
                </p>
              ) : null}
            </>
          ) : (
            <p className="mm" data-testid="location-detail-poi" style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
              A scout&apos;s find. Its photographs and exact position are shared once it is brought onto
              the platform as a listing.
            </p>
          )}

          <section aria-labelledby="location-detail-fit">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h3 id="location-detail-fit" className="flbl" style={{ margin: 0 }}>
                {`Against “${hit.title}”`}
              </h3>
              <Coverage requirement={hit} />
            </div>
            <WhyItMatches requirement={hit} />
          </section>

          {isListing ? (
            <section aria-label="Where it is" data-testid="location-detail-map">
              <LocationMap locations={[location]} variant="single" />
            </section>
          ) : null}
        </div>

        <footer
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            padding: '14px 20px',
            borderTop: '1px solid var(--color-divider)',
            background: 'var(--color-surface)',
          }}
        >
          {rejected ? (
            onRestore === undefined ? (
              <span className="mm" data-testid="location-detail-rejected" style={{ fontSize: 13, alignSelf: 'center' }}>
                Rejected
              </span>
            ) : (
              // Restore is a LONGLIST write: the server asks for CANDIDATE_EDIT
              // (Shortlist's permission), not CANDIDATE_REJECT.
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="location-detail-restore"
                disabled={busy || shortlistRefusal !== null}
                title={shortlistRefusal ?? 'Puts this place back among the candidates'}
                onClick={onRestore}
              >
                Restore
              </button>
            )
          ) : (
            <>
              <button
                type="button"
                className="btn btn-primary"
                data-testid="location-detail-shortlist"
                disabled={busy || shortlisted || shortlistRefusal !== null}
                title={shortlistRefusal ?? undefined}
                onClick={onShortlist}
              >
                {shortlisted ? 'Shortlisted' : 'Shortlist'}
              </button>
              <RejectWithReason
                testId="location-detail-reject"
                className="btn btn-ghost"
                placement="up"
                disabled={busy || rejectRefusal !== null}
                title={rejectRefusal ?? undefined}
                onReject={onReject}
              />
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
